import { type FC, useEffect, useMemo, useState } from "react";

import {
  type PropertyCondition,
  type TraitConfig,
  type TraitType,
  isInlineTraitColumn,
} from "@hightouch/lib/query/visual/types";
import {
  Alert,
  Button,
  ChakraModal,
  ChakraModalBody,
  ChakraModalContent,
  ChakraModalFooter,
  ChakraModalHeader,
  ChakraModalOverlay,
  CloseIcon,
  Column,
  FormField,
  Heading,
  IconButton,
  Row,
  Spinner,
  Text,
  TextInput,
  Textarea,
  useToast,
} from "@hightouch/htui";
import { yupResolver } from "@hookform/resolvers/yup";
import { captureException } from "src/lib/monitoring";
import immutableUpdate, { type Spec } from "immutability-helper";
import { Controller, useForm } from "react-hook-form";

import { CalculationSummary } from "src/components/traits/calculation-summary";
import { PreviewTrait } from "src/components/traits/preview-trait";
import { InlinePreviewableTraitType } from "src/components/traits/use-preview-trait";
import { useFormErrorContext } from "src/contexts/form-error-context";
import {
  type CreateTraitDefinitionInput,
  useCreateTraitMutation,
} from "src/graphql";
import { getInitialTraitColumn, isRelatedColumn } from "src/types/visual";
import {
  type CalculationMethod,
  isDuplicateTraitNameError,
} from "src/components/traits/utils";
import { useHydrateTraitsWithFilterableAudienceColumns } from "src/hooks/use-fetch-traits-with-filterable-audience-columns";
import type { FullParentModel } from "src/components/audiences/types";
import { createTraitValidationSchema } from "src/pages/traits/create-trait-schema";
import { useQueryClient } from "react-query";

type Props = {
  condition: PropertyCondition;
  parent: FullParentModel;
  onClose: () => void;
  onTraitCreated: (conditionWithTrait: PropertyCondition) => Promise<void>;
};

type FormProps = {
  name: string;
  description: string;
  segment_id: string;
  relationship_id: string;
  calculation_method: CalculationMethod;
  type: TraitType;
  config: TraitConfig;
  is_template: boolean;
  sourceType: string;
};

/*
 * Takes a condition with an InlineTraitColumn and creates a top-level trait
 */
export const CreateTraitModal: FC<Readonly<Props>> = ({
  condition,
  parent,
  onClose,
  onTraitCreated,
}) => {
  const { hasValidationErrors } = useFormErrorContext();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const queryCache = useQueryClient();
  // Need to get the filterable columns on the trait relationships
  const {
    traitsWithFilterableColumns,
    isLoading: areFilterableColumnsLoading,
  } = useHydrateTraitsWithFilterableAudienceColumns(parent.traits);

  const form = useForm<FormProps>({
    defaultValues: {
      is_template: false,
      sourceType: parent.connection?.type,
    },
    resolver: yupResolver(createTraitValidationSchema),
  });
  const { control, reset, watch } = form;
  // biome-ignore lint/suspicious/noTsIgnore: legacy suppression
  // @ts-ignore - no circular types until react-hook-form v8
  const name = watch("name");
  const type = watch("type");
  const config = watch("config");
  const relationshipId = watch("relationship_id");

  const parentModelWithFilterableTraitColumns = useMemo(() => {
    return {
      ...parent,
      traits: traitsWithFilterableColumns,
    };
  }, [parent, traitsWithFilterableColumns]);

  const relatedModel = parent?.relationships.find(
    ({ id }) => id === relationshipId,
  )?.to_model;

  useEffect(() => {
    const property = condition.property;

    if (isRelatedColumn(property) && isInlineTraitColumn(property.column)) {
      const inlineTrait = property.column;
      reset({
        type: inlineTrait.traitType,
        segment_id: parent?.id,
        relationship_id: inlineTrait.relationshipId,
        config: {
          ...inlineTrait.traitConfig,
          conditions: inlineTrait.conditions,
        },
        is_template: false,
        sourceType: parent.connection?.type,
      });
    }
  }, [reset, parent?.id, parent.connection?.type, condition.property]);

  const { mutateAsync: createTrait } = useCreateTraitMutation({
    onSuccess: () => {
      // XXX: This callback is intentionally blank.
      // After a mutation finishes, all active queries get invalidated.
      // That causes the audience query builder to refresh its query and we lose our in-progress conditions.
      // We want to retain them so that we can inject the newly created trait into the condition.
      // Adding an onSuccess to the mutation hook prevents this issue from happening.
      // We only invalidate the relevant traits query in order to refetch them.
      queryCache.invalidateQueries({ queryKey: ["TraitsForParentModel"] });
    },
  });

  const wrappedOnSubmit = async (data: FormProps) => {
    try {
      if (hasValidationErrors()) {
        setShowValidationAlert(true);
        toast({
          id: "create-trait",
          title: "There was a problem saving your trait",
          message: "Please check your configuration and try again",
          variant: "error",
        });
        return;
      }

      setSubmitting(true);

      const traitInput: CreateTraitDefinitionInput = {
        name: data.name,
        description: data.description,
        segmentId: data.segment_id,
        relationshipId: data.relationship_id,
        type: data.type,
        config: data.config,
        isTemplate: false,
      };

      const createdTrait = await createTrait({
        input: traitInput,
      });

      toast({
        id: "create-trait",
        title: "Trait successfully created",
        variant: "success",
      });

      // Inject the newly created trait into the condition
      if (createdTrait.createTraitDefinition) {
        const updatedCondition = immutableUpdate(condition, {
          property: {
            $set: getInitialTraitColumn(createdTrait.createTraitDefinition),
          },
          propertyOptions: {
            $merge: {
              traitType: "trait",
            },
          },
        } as Spec<PropertyCondition>);

        await onTraitCreated(updatedCondition);
      }

      onClose();
    } catch (error) {
      toast({
        id: "create-trait",
        title: "Trait failed to be created",
        message: isDuplicateTraitNameError(error)
          ? `There is an existing trait named "${data.name}" associated with this parent model. Please choose a different name and try again.`
          : error.message,
        variant: "error",
      });
      captureException(error);
    } finally {
      setSubmitting(false);
    }
  };

  const onError = () => {
    toast({
      id: "create-trait",
      variant: "error",
      title: "Trait failed to be saved",
      message: "There were errors in your submission",
    });
  };

  return (
    <ChakraModal
      closeOnEsc
      closeOnOverlayClick
      isCentered
      scrollBehavior="inside"
      isOpen
      onClose={onClose}
    >
      <ChakraModalOverlay />
      <ChakraModalContent
        maxHeight="90vh"
        maxWidth="700px"
        padding={0}
        my="auto"
      >
        <ChakraModalHeader
          alignItems="center"
          padding={6}
          borderBottomWidth="hairline"
          borderBottomStyle="solid"
          borderColor="base.border"
        >
          <Row alignItems="center" justifyContent="space-between" width="100%">
            <Heading>New trait</Heading>
            <IconButton
              aria-label="Close modal"
              color="text.secondary"
              icon={CloseIcon}
              onClick={onClose}
            />
          </Row>
        </ChakraModalHeader>

        <ChakraModalBody m={0}>
          <Column flex={1} p={6} gap={6} overflowY="auto">
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <FormField
                  label="Name"
                  tip="Shows up in the audience query builder when selecting from a list of traits."
                  error={error?.message}
                >
                  <TextInput
                    placeholder="Enter a name"
                    width="100%"
                    {...field}
                  />
                </FormField>
              )}
            />
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <FormField label="Description">
                  <Textarea
                    placeholder="Enter a description"
                    width="100%"
                    {...field}
                  />
                </FormField>
              )}
            />

            <Column gap={2}>
              <Text fontWeight="medium" mb={1}>
                Calculation summary
              </Text>
              {areFilterableColumnsLoading ? (
                <Spinner />
              ) : relatedModel ? (
                <CalculationSummary
                  parentModel={parentModelWithFilterableTraitColumns}
                  relatedModel={relatedModel}
                  type={type}
                  config={config}
                  // Currently not possible to create an inline predictive trait
                  predictor={undefined}
                />
              ) : null}
            </Column>

            <PreviewTrait
              isLoading={areFilterableColumnsLoading}
              parentModel={parentModelWithFilterableTraitColumns}
              trait={{
                name,
                type: type as InlinePreviewableTraitType,
                config,
                relationshipId,
              }}
            />

            {showValidationAlert && (
              <Alert
                variant="inline"
                type="error"
                title="Incomplete trait"
                message="Please check your trait calculation and fix all errors before saving."
              />
            )}
          </Column>
        </ChakraModalBody>

        <ChakraModalFooter
          borderTop="solid 1px"
          borderTopColor="base.border"
          bg="white"
          m={0}
          px={6}
          py={4}
          gap={3}
          borderRadius="inherit"
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button
            isDisabled={submitting || showValidationAlert}
            isLoading={submitting}
            variant="primary"
            onClick={form.handleSubmit(wrappedOnSubmit, onError)}
          >
            Save trait
          </Button>
        </ChakraModalFooter>
      </ChakraModalContent>
    </ChakraModal>
  );
};
